/**
 * POS Data Preloader
 * Silently fetches and caches all POS data immediately after login,
 * so the POS page loads instantly when the user navigates to it.
 */
import { api } from "@/store/useStore";
import { cacheProducts, cacheCategories } from "@/lib/offline-storage";
import * as offlineDB from "@/lib/offline-storage";

let preloadInProgress = false;

/**
 * Preload all POS data in background after login.
 * Safe to call multiple times — deduplicates automatically.
 */
export async function preloadPOSData(user) {
  if (preloadInProgress) return;
  preloadInProgress = true;

  const start = performance.now();

  try {
    // Batch 1: Core catalog data (parallel)
    const [categories, products, outlets, groups] = await Promise.all([
      api.get("/categories").catch(() => []),
      api.get("/products?for_pos=true").catch(() => []),
      api.get("/outlets").catch(() => []),
      api.get("/products/groups").catch(() => []),
    ]);

    // Cache products & categories in IndexedDB
    if (products.length > 0) await cacheProducts(products);
    if (categories.length > 0) await cacheCategories(categories);

    // Cache outlets
    const userOutletIds = user?.outlet_ids || [];
    const accessibleOutlets =
      userOutletIds.length > 0
        ? outlets.filter((o) => userOutletIds.includes(o.id))
        : outlets;
    await offlineDB.cacheData("outlets", accessibleOutlets, 120);

    // Batch 2: Outlet-specific data (if user has an outlet)
    const outlet = accessibleOutlets[0];
    if (outlet?.id) {
      const [outletDetails, template, destinations] = await Promise.all([
        api.get(`/outlets/${outlet.id}`).catch(() => null),
        api.get(`/outlets/${outlet.id}/invoice-template`).catch(() => null),
        api.get(`/outlets/${outlet.id}/print-destinations`).catch(() => []),
      ]);

      if (outletDetails)
        await offlineDB.cacheData(`outlet_${outlet.id}`, outletDetails, 120);
      if (template)
        await offlineDB.cacheData(`template_${outlet.id}`, template, 120);
      if (destinations.length > 0)
        await offlineDB.cacheData(
          `destinations_${outlet.id}`,
          destinations,
          120
        );

      // Outlet-filtered menu with per-outlet pricing
      try {
        const menuData = await api.get(`/outlets/${outlet.id}/menu`);
        const posProducts = menuData.map((p) => ({
          ...p,
          price: p.effective_price ?? p.price,
        }));
        await cacheProducts(posProducts);
      } catch {
        // Already cached generic products above
      }

      // Outlet-filtered categories
      const visibleCategories = categories.filter((cat) => {
        const vis = cat.visible_outlet_ids || [];
        return vis.length === 0 || vis.includes(outlet.id);
      });
      if (visibleCategories.length > 0) await cacheCategories(visibleCategories);

      // Batch 3: Property settings (tax, payment integrations)
      const propertyId = outlet.property_id;
      if (propertyId) {
        try {
          const properties = await api.get("/properties");
          const property = properties.find((p) => p.id === propertyId);
          if (property) {
            const taxes = await api.get("/taxes").catch(() => []);
            const activeTaxes = taxes.filter((t) => t.is_active);
            const totalRate = activeTaxes.reduce(
              (sum, t) => sum + (t.rate || 0),
              0
            );

            const mpesaConfig = property.integrations?.mpesa;
            const cybersourceConfig = property.integrations?.cybersource;

            await offlineDB.cacheData(
              "property_settings",
              {
                taxMethod: property.tax_calculation_method || "exclusive",
                taxRate: totalRate || 16,
                mpesaSettings: mpesaConfig?.enabled ? mpesaConfig : null,
                cardSettings: cybersourceConfig?.enabled
                  ? { ...cybersourceConfig, property_id: propertyId }
                  : null,
              },
              120
            );
          }
        } catch {
          // Non-critical — POS will fetch on demand
        }
      }
    }

    const elapsed = (performance.now() - start).toFixed(0);
  } catch (error) {
  } finally {
    preloadInProgress = false;
  }
}
